import { DatabaseZap, FileSliders } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import type { LayoutContextType } from '@/components/layout/main-layout';
import type { SidebarGroupData, TabItem } from '@/types';

import StartView from '@/components/start-view';
import CustomSidebar from '@/components/ui/custom-sidebar-content';
import RTabs from '@/components/ui/rtabs';
import { SidebarProvider } from '@/components/ui/sidebar';
import useBreakpoint from '@/hooks/use-break-point';
import AdminSidebarHeader from '@/pages/admin-control-panel/admin-sidebar-header';
import RenderActiveContent from '@/pages/admin-control-panel/render-active-content';
import useReleaseNoteStore from '@/store/useReleaseNoteStore';
import { INTERFACE_STATUS, RELEASE_NOTES } from '@/utils/constants';
import {
  SEARCH_PARAMS_ADMIN_CONTROL_TAB,
  SEARCH_PARAMS_LIMIT,
  SEARCH_PARAMS_PAGE,
} from '@/utils/constants/queries';

const VALID_VIEWS = [INTERFACE_STATUS, RELEASE_NOTES];

const AdminControlPanel = () => {
  const { t } = useTranslation();
  const isTablet: boolean = useBreakpoint('tablet');

  const [searchParam, setSearchParam] = useSearchParams();
  const { setIsAppLoading } = useReleaseNoteStore();

  const currentTabParam = searchParam.get(SEARCH_PARAMS_ADMIN_CONTROL_TAB);

  const activeView =
    currentTabParam && VALID_VIEWS.includes(currentTabParam) ? currentTabParam : INTERFACE_STATUS;

  useEffect(() => {
    if (currentTabParam && !VALID_VIEWS.includes(currentTabParam)) {
      setSearchParam(
        (prev) => {
          prev.set(SEARCH_PARAMS_ADMIN_CONTROL_TAB, INTERFACE_STATUS);

          return prev;
        },
        { replace: true }
      );
    }
  }, [currentTabParam, setSearchParam]);

  const { setIsTopNavVisible } = useOutletContext<LayoutContextType>() || {
    setIsTopNavVisible: () => {},
  };

  const [isAnimationFinished, setIsAnimationFinished] = useState(false);

  useEffect(() => {
    setIsTopNavVisible(isAnimationFinished);
    setIsAppLoading(!isAnimationFinished);

    return () => {
      setIsTopNavVisible(true);
    };
  }, [isAnimationFinished, setIsTopNavVisible]);

  if (!isAnimationFinished) {
    return <StartView onFinish={() => setIsAnimationFinished(true)} />;
  }

  const handleViewChange = (value: string) => {
    setSearchParam((prev) => {
      prev.set(SEARCH_PARAMS_ADMIN_CONTROL_TAB, value);

      if (value !== RELEASE_NOTES) {
        prev.delete('id');
        prev.delete(SEARCH_PARAMS_PAGE);
        prev.delete(SEARCH_PARAMS_LIMIT);
      }

      return prev;
    });
  };

  const adminSidebarGroups: SidebarGroupData[] = [
    {
      id: 'admin-main-group',
      items: [
        {
          id: INTERFACE_STATUS,
          type: 'action',
          title: t('sidebar.interface_status'),
          icon: <DatabaseZap className="size-5" />,
          onClick: () => handleViewChange(INTERFACE_STATUS),
        },
        {
          id: RELEASE_NOTES,
          type: 'action',
          title: t('sidebar.manage_release_notes'),
          icon: <FileSliders className="size-5" />,
          onClick: () => handleViewChange(RELEASE_NOTES),
        },
      ],
    },
  ];

  const tabItems: TabItem[] = [
    {
      value: INTERFACE_STATUS,
      label: t('sidebar.interface_status'),
      content: <RenderActiveContent activeView={INTERFACE_STATUS} />,
    },
    {
      value: RELEASE_NOTES,
      label: t('sidebar.manage_release_notes'),
      content: <RenderActiveContent activeView={RELEASE_NOTES} />,
    },
  ];

  if (isTablet) {
    return (
      <div className="absolute inset-0 pt-16 flex flex-col bg-white">
        <RTabs
          className="pt-5 px-1"
          value={activeView}
          onValueChange={handleViewChange}
          tabItems={tabItems}
          tabContentClassName="pt-6 px-1"
        />
      </div>
    );
  }

  return (
    <SidebarProvider className="fixed pt-16 h-full min-h-[calc(100vh-64px)] bg-white">
      <CustomSidebar
        collapsible="none"
        className="w-64 lg:w-72"
        header={<AdminSidebarHeader />}
        groups={adminSidebarGroups}
        activeId={activeView}
      >
        <RenderActiveContent activeView={activeView} />
      </CustomSidebar>
    </SidebarProvider>
  );
};

export default AdminControlPanel;
